import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Link from '@atlaskit/link/link';
import SectionMessage from '@atlaskit/section-message/message';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';

import { messages } from './messages';
import type { MessageFrontendProps } from './types';
import { useTracedContactSalesLink } from './use-traced-contact-sales-link';

export const AdminhubBillingPreviewPlsContactSalesMessage: (
	props: MessageFrontendProps,
) => JSX.Element = ({ accountSegment, contactSalesUrl, onDismiss }) => {
	const { messageInstanceViewed } = useMessageInstanceEvent();
	const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
	const { messageDismissed, messageViewed } = useMessageEvent();
	const contactSalesLink = useTracedContactSalesLink(contactSalesUrl);

	const sharedMetadata = useMemo(
		() => ({
			touchpoint: 'billingpreview',
			accountSegment,
		}),
		[accountSegment],
	);

	// `ManualMessageViewedEvent` below suppresses the viewed events Post Office fires for us,
	// which is the only way to attach metadata to them — so fire both here instead. Guarded by a
	// ref because `messageInstanceViewed`/`messageViewed` are `useCallback`s over context values,
	// and a re-created identity would otherwise re-report the view.
	const hasReportedView = useRef(false);
	useEffect(() => {
		if (hasReportedView.current) {
			return;
		}
		hasReportedView.current = true;
		void messageViewed();
		void messageInstanceViewed(sharedMetadata);
	}, []);

	const handleContactSales = useCallback(() => {
		void onCtaActioned({
			interactionIdentifier: 'contact-sales',
			destinationUrl: contactSalesLink.url,
			additionalMetadata: {
				...sharedMetadata,
				...contactSalesLink.metadata,
			},
		});
	}, [contactSalesLink, onCtaActioned, sharedMetadata]);

	const handleDismiss = useCallback(() => {
		void onClosed({
			interactionIdentifier: 'dismiss',
			additionalMetadata: { ...sharedMetadata },
		});

		void messageDismissed();

		onDismiss?.();
	}, [messageDismissed, onClosed, onDismiss, sharedMetadata]);

	return (
		<ManualMessageViewedEvent>
			<SectionMessage appearance="discovery" isDismissible onDismiss={handleDismiss}>
				<FormattedMessage
					{...messages.body}
					values={{
						Link: (chunks) => (
							<Link href={contactSalesLink.url} onClick={handleContactSales} target="_blank">
								{chunks}
							</Link>
						),
					}}
				/>
			</SectionMessage>
		</ManualMessageViewedEvent>
	);
};
